import { Handle, Position } from '@xyflow/react'
import { Code2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { GraphNodeData } from '@/types/graph'
import { useCanvasStore } from '@store/canvas'

interface CodeNodeProps {
  data: GraphNodeData
  selected?: boolean
  id: string
}

export default function CodeNode({ data, selected, id }: CodeNodeProps) {
  const { t } = useTranslation()
  const removeNode = useCanvasStore((state) => state.removeNode)

  const code = typeof data.config?.code === 'string' ? data.config.code : ''
  const language = typeof data.config?.language === 'string' ? data.config.language : 'javascript'
  const preview = code.length > 120 ? code.slice(0, 120) + '…' : code

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    removeNode(id)
  }

  return (
    <div
      className={`px-4 py-3 rounded-lg border-2 bg-white shadow-md min-w-48 transition-all ${
        selected
          ? 'border-amber-600 ring-2 ring-amber-600/30 shadow-lg'
          : 'border-amber-600/50 hover:border-amber-600'
      }`}
    >
      <Handle type="target" position={Position.Top} />

      {/* Header */}
      <div className="flex items-center justify-between mb-2 group">
        <div className="flex items-center gap-2 flex-1">
          <Code2 size={16} className="text-amber-600 flex-shrink-0" />
          <div className="font-bold text-sm text-gray-900 truncate">
            {data.label || 'Code'}
          </div>
        </div>
        <button
          onClick={handleDelete}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-100 rounded text-red-600"
          title={t('build.deleteNode')}
        >
          <X size={14} />
        </button>
      </div>

      {/* Body */}
      <div className="text-xs text-gray-600 mb-2 bg-gray-50 rounded p-2 max-h-20 overflow-y-auto">
        {preview ? (
          <pre className="whitespace-pre-wrap font-mono text-[11px] text-gray-700 leading-relaxed">
            {preview}
          </pre>
        ) : (
          <div className="text-gray-500">{t('build.noCodeConfigured', 'Aucun code configuré')}</div>
        )}
      </div>

      {/* Footer */}
      <div className="flex gap-2 text-xs">
        <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded">
          {language}
        </span>
        {data.config?.output_key && (
          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded truncate">
            → {String(data.config.output_key)}
          </span>
        )}
      </div>

      <Handle type="source" position={Position.Bottom} />
    </div>
  )
}
